import { useMemo } from 'react'
import { BatteryCharging, Building2, DoorClosed, DoorOpen, Flame, Lock, LockOpen, PlugZap } from 'lucide-react'
import { Shell, useShellMeta } from '../../../admin/Shell.js'
import { Link } from '../../../router.js'
import { fmtDateHeure } from '../../api.js'
import { fmtHeure, fmtNombre, fmtRatio, nomSalle, Pastille, PastilleEtat, TON_ALIM, TON_SERVICE, useVueSonde } from '../commun.js'
import {
  Bandeaux, donneeDe, EnTeteDomaine, Journal, LienAppareil, Lu, Panneau, Sources, Tuile, Tuiles, useADecider, useEvenements, usePeutDetails, Vide, Zone,
} from '../video/kit.js'
import {
  compterADecider, estListeAppareils, fmtDecimal, lienIncident, lienSalle, texteAutorise, texteEvenement, tonMesure,
  type AppareilBref, type SourceDomaine,
} from '../video/kit-modele.js'
import {
  accesUnifi, alarmesFumee, compteurs, detecteursRanges, estPowerDoc, estServiceDoc, estVueGenerique, etatOnduleur, finAutonomie,
  mesuresOnduleur, santeHa, tonAcces,
  type AccesUnifi, type Onduleur, type PowerDoc, type ServiceDoc,
} from './batiment-modele.js'
import { useMenuSalle } from '../menu-salle.js'
import { CHEMIN_ONDULEURS } from './onduleurs-modele.js'
import './batiment.css'

/**
 * TECHNIQUE › BÂTIMENT (21/10/2026) — `GET /v1/power`, Home Assistant
 * (`/v1/services/home-assistant`), détecteurs de fumée
 * (`/v1/devices?family=fire_safety` et événements SMOKE_*).
 *
 * Les détecteurs de fumée EN TÊTE. Puis l'onduleur — sur secteur ou sur
 * batterie, autonomie et fin estimée ; une coupure secteur est UN incident,
 * celui de Sonde —, les portes (UniFi Access, lues dans `GET /v1/video`),
 * Home Assistant, l'énergie et l'alimentation des salles.
 */

const KINDS_FUMEE = ['SMOKE_DETECTED', 'SMOKE_CLEARED']
const KINDS = ['SMOKE_DETECTED', 'SMOKE_CLEARED', 'MAINS_OUTAGE', 'MAINS_RESTORED', 'UPS_LOW_BATTERY', 'UPS_RUNTIME_LOW', 'UPS_OVERLOAD', 'UPS_REPLACE_BATTERY', 'HA_ENTITY_REMOVED', 'HA_TRACKING_CAPPED', 'POWER_DOMAIN_LEARNED']

export function BatimentPage() {
  const meta = useShellMeta()
  const details = usePeutDetails()
  const pw = useVueSonde('power', estPowerDoc)
  const ha = useVueSonde('services/home-assistant', estServiceDoc, 30_000)
  const fum = useVueSonde('devices?family=fire_safety&limit=500', estListeAppareils, 30_000)
  const evFumee = useEvenements(KINDS_FUMEE, 50)
  const ev = useEvenements(KINDS, 25)
  const decouvertes = useADecider('home_assistant')
  const vid = useVueSonde('video', estVueGenerique, 30_000)

  const P = donneeDe(pw.lecture, pw.precedente)
  const H = donneeDe(ha.lecture, ha.precedente)
  const F = donneeDe(fum.lecture, fum.precedente)
  const E = donneeDe(evFumee.lecture, evFumee.precedente)
  const alarmes = useMemo(() => (E.d ? alarmesFumee(E.d.items) : []), [E.d])
  const V = donneeDe(vid.lecture, vid.precedente)
  const acces = useMemo(() => (V.d ? accesUnifi(V.d.generic) : null), [V.d])
  // Le vert « aucune alarme » n'est dit que sur un journal relu À L'INSTANT.
  const journalFumee: 'ok' | 'lecture' | 'echec' = !evFumee.lecture ? 'lecture' : evFumee.lecture.etat === 'ok' ? 'ok' : 'echec'

  const sources = useMemo(() => {
    const m = new Map<string, SourceDomaine>()
    for (const s of [...(P.d?.sources ?? []), ...(H.d?.service.sources ?? [])]) if (!m.has(s.code)) m.set(s.code, s)
    return [...m.values()]
  }, [P.d, H.d])

  return (
    <Shell active="technique" meta={meta}>
      <main className="lc-page lc-page--wide lc-stagger sv-dom-page sv-bat">
        <EnTeteDomaine
          retour={{ href: '/technique', label: 'Technique' }}
          icone={<Building2 size={26} />}
          titre="Bâtiment"
          sousTitre="Détecteurs de fumée, onduleur et coupures de courant, Home Assistant et compteurs d’énergie. Lu sur Sonde, sans rien modifier."
          genereA={P.d?.generated_at}
          perime={P.perime}
        />
        <Bandeaux lecture={pw.lecture} genereA={P.d?.generated_at} collector={P.d?.collector} />

        <Fumee detecteurs={F.d?.items ?? null} perime={F.perime} lectureMessage={fum.lecture && fum.lecture.etat !== 'ok' ? fum.lecture.message : null}
               alarmes={alarmes} journal={journalFumee} aDecider={compterADecider(decouvertes, ['smoke_detector'])} />

        {P.d ? (
          <Zone perime={P.perime}>
            <Alimentation d={P.d} />
          </Zone>
        ) : !pw.lecture ? <p className="lc-hint">Lecture en cours…</p> : null}

        <Panneau id="sv-bat-acces" titre="Portes (UniFi Access)" indice="gâches et capteurs de position, lus par Sonde">
          {V.d ? <Zone perime={V.perime}><Acces a={acces} details={details} /></Zone>
            : <p className="lc-hint">{vid.lecture && vid.lecture.etat !== 'ok' ? vid.lecture.message : 'Lecture en cours…'}</p>}
        </Panneau>

        <div className="sv-dom-colonnes">
          <Panneau id="sv-bat-ha" titre="Home Assistant" indice="la domotique qui remonte détecteurs et compteurs">
            {H.d ? <Zone perime={H.perime}><HomeAssistant d={H.d} /></Zone>
              : <p className="lc-hint">{ha.lecture && ha.lecture.etat !== 'ok' ? ha.lecture.message : 'Lecture en cours…'}</p>}
          </Panneau>
          <Panneau id="sv-bat-energie" titre="Énergie" indice="compteurs lus dans Home Assistant">
            {P.d ? <Zone perime={P.perime}><Energie d={P.d} details={details} /></Zone> : <p className="lc-hint">—</p>}
          </Panneau>
        </div>

        {P.d ? (
          <div className="sv-dom-colonnes">
            <Panneau id="sv-bat-salles" titre="Courant des salles" indice="éteinte comme prévu n’est jamais une panne">
              <Zone perime={P.perime}><SallesCourant d={P.d} /></Zone>
            </Panneau>
            <Panneau id="sv-bat-poe" titre="Budgets PoE" indice="puissance fournie par les switchs aux appareils">
              <Zone perime={P.perime}><Poe d={P.d} /></Zone>
            </Panneau>
          </div>
        ) : null}

        <div className="sv-dom-colonnes">
          <Panneau id="sv-bat-journal" titre="Journal du bâtiment" indice="dernières 24 h, message de Sonde">
            <Journal lecture={ev.lecture} precedente={ev.precedente} vide="Aucun événement du bâtiment dans les dernières 24 heures." />
          </Panneau>
          <Panneau id="sv-bat-sources" titre="Sources" indice="d’où viennent ces chiffres">
            <Sources sources={sources} details={details} />
          </Panneau>
        </div>
      </main>
    </Shell>
  )
}

/* ─────────────────────────────── Détecteurs de fumée (en tête) */

function Fumee({ detecteurs, perime, lectureMessage, alarmes, journal, aDecider }: {
  detecteurs: AppareilBref[] | null; perime: boolean; lectureMessage: string | null
  alarmes: ReturnType<typeof alarmesFumee>; journal: 'ok' | 'lecture' | 'echec'; aDecider: number | null
}) {
  const ranges = detecteurs ? detecteursRanges(detecteurs) : []
  const enLigne = ranges.filter((d) => d.state.display_state === 'ONLINE').length
  const horsLigne = ranges.filter((d) => d.state.display_state === 'OFFLINE').length
  const autres = ranges.length - enLigne - horsLigne
  return (
    <Panneau id="sv-bat-fumee" titre="Détecteurs de fumée" className={alarmes.length ? 'sv-bat-fumee--alarme' : undefined}
             indice={detecteurs && detecteurs.length ? `${detecteurs.length} suivi${detecteurs.length > 1 ? 's' : ''} : ${enLigne} en ligne${horsLigne ? `, ${horsLigne} hors ligne` : ''}${autres ? `, ${autres} autre${autres > 1 ? 's' : ''}` : ''}` : undefined}>
      {alarmes.length ? (
        <ul className="sv-bat-alarmes" role="alert">
          {alarmes.map((a) => (
            <li key={`${a.id}`} className="sv-bat-alarme">
              <Flame size={20} aria-hidden="true" />
              <span className="sv-bat-alarme__corps">
                <strong>{texteEvenement(a)}</strong>
                <span className="sv-bat-alarme__meta">
                  Depuis {fmtHeure(a.at)}{a.zone_code ? ` · ${nomSalle(a.zone_code)}` : ''}
                  {a.uid ? <> · <LienAppareil uid={a.uid} nom={a.display_name ?? a.uid} /></> : null}
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {lectureMessage && !detecteurs ? <p className="lc-hint">{lectureMessage}</p> : null}
      {detecteurs && detecteurs.length === 0 ? (
        <Vide titre="Aucun détecteur de fumée n’est encore suivi par Sonde." versADecider>
          <p>
            {aDecider ? `Home Assistant en propose ${aDecider} : ils attendent d’être validés dans « À valider ». ` : 'Ceux que Home Assistant connaît attendent d’être validés dans « À valider ». '}
            Tant qu’ils ne le sont pas, leur état n’est pas suivi un à un ici.
          </p>
        </Vide>
      ) : null}
      {ranges.length ? (
        <Zone perime={perime}>
          {alarmes.length ? null : journal === 'ok'
            ? <p className="sv-bat-calme"><Pastille ton="ok">Aucune alarme de fumée dans les dernières 24 heures</Pastille></p>
            : <p className="lc-hint sv-bat-calme">{journal === 'lecture' ? 'Lecture du journal des alarmes de fumée…' : 'Alarmes de fumée : le journal de Sonde n’a pas pu être relu à l’instant. Rien n’est affirmé tant qu’il ne l’est pas.'}</p>}
          <ul className="sv-bat-detecteurs">
            {ranges.map((d) => (
              <li key={d.uid} className={`sv-dom-ligne${d.state.display_state === 'OFFLINE' ? ' sv-dom-ligne--err' : ''}`}>
                <span className="sv-dom-ligne__nom"><LienAppareil uid={d.uid} nom={d.display_name} zone={d.zone_code} /></span>
                <PastilleEtat etat={d.state.display_state} libelle={d.state.display_label} />
                <span className="sv-dom-ligne__meta">{d.zone_code ? `${nomSalle(d.zone_code)} · ` : ''}<Lu age={d.state.data_age_s} source="Home Assistant" /></span>
              </li>
            ))}
          </ul>
        </Zone>
      ) : null}
    </Panneau>
  )
}

/* ─────────────────────────────── Onduleur et coupure secteur */

function Alimentation({ d }: { d: PowerDoc }) {
  const nut = d.sources.find((s) => s.code === 'nut') ?? null
  const coupure = d.mains_outage
  const surBatterie = d.ups.find((u) => u.on_battery === true) ?? null
  const fin = surBatterie ? finAutonomie(surBatterie, Date.now()) : null
  const generiques = mesuresOnduleur(d.generic)
  const seuil = d.thresholds['thresholds.power.ups_runtime_min']

  return (
    <Panneau id="sv-bat-onduleur" titre="Onduleur et courant" indice={seuil?.warn != null ? `alerte sous ${seuil.warn} min d’autonomie` : undefined}
             droite={<Link className="sv-dom-lien" href={CHEMIN_ONDULEURS}>Onduleurs, historique et autonomie par appareil</Link>}
             className={coupure.active ? 'sv-bat-coupure--active' : undefined}>
      {coupure.active ? (
        <div className="sv-bat-coupure" role="alert">
          <PlugZap size={22} aria-hidden="true" />
          <div className="sv-bat-coupure__corps">
            <p className="sv-bat-coupure__titre">
              Coupure secteur{coupure.since ? ` depuis ${fmtHeure(coupure.since)}` : ''}
              {fin ? ` : environ ${fmtDecimal(fin.reste_min, 'min', 0)} d’autonomie, fin estimée vers ${fmtHeure(fin.fin.toISOString())}.` : '.'}
            </p>
            <p className="sv-bat-coupure__meta">
              {coupure.devices != null ? `${fmtNombre(coupure.devices)} appareil${coupure.devices > 1 ? 's' : ''} coupé${coupure.devices > 1 ? 's' : ''} par cette coupure, comptés dans un seul incident. ` : 'Un seul incident pour toute la coupure. '}
              {coupure.incident_id ? <Link className="sv-dom-lien" href={lienIncident(coupure.incident_id)}>Ouvrir l’incident</Link> : null}
            </p>
          </div>
        </div>
      ) : d.ups.some((u) => u.on_battery === false) ? (
        <p className="sv-bat-calme"><Pastille ton="ok">Secteur présent : l’onduleur est sur secteur</Pastille></p>
      ) : (
        <p className="lc-hint">Aucune coupure secteur constatée par Sonde.</p>
      )}

      {d.ups.length ? (
        <div className="sv-bat-onduleurs">
          {d.ups.map((u) => <CarteOnduleur key={u.device.uid} u={u} />)}
        </div>
      ) : (
        <Vide titre="L’onduleur n’est pas encore au registre de Sonde." versADecider>
          <p>
            {nut?.status === 'OK' ? 'La source « Onduleur (NUT) » répond : ses mesures générales sont lues, mais sur secteur ou sur batterie, charge et charge utile ne s’affichent qu’une fois l’onduleur validé.' : 'La source « Onduleur (NUT) » ne répond pas pour l’instant.'}
          </p>
          {generiques.length ? (
            <dl className="sv-dom-faits">
              {generiques.map((g) => (
                <div key={`${g.entity_key}:${g.metric}`}><dt>{g.label}</dt><dd>{fmtDecimal(g.value, g.unit ?? '', 0)} <Lu age={g.data_age_s} /></dd></div>
              ))}
            </dl>
          ) : null}
        </Vide>
      )}
    </Panneau>
  )
}

function CarteOnduleur({ u }: { u: Onduleur }) {
  const e = etatOnduleur(u)
  return (
    <article className={`sv-bat-onduleur sv-bat-onduleur--${e.ton}`}>
      <header className="sv-bat-onduleur__tete">
        <span className="sv-bat-onduleur__nom"><BatteryCharging size={18} aria-hidden="true" /> <LienAppareil uid={u.device.uid} nom={u.device.display_name} zone={u.device.zone_code} /></span>
        <Pastille ton={e.ton}>{e.libelle}</Pastille>
      </header>
      <p className="sv-bat-onduleur__autonomie">
        <span className="sv-bat-onduleur__chiffre">{fmtDecimal(u.runtime_min, '', 0)}</span> <span className="sv-bat-onduleur__unite">min d’autonomie</span>
      </p>
      <dl className="sv-dom-faits">
        <div><dt>Charge batterie</dt><dd>{fmtDecimal(u.charge_pct, '%', 0)}</dd></div>
        <div><dt>Charge utile</dt><dd>{fmtDecimal(u.load_pct, '%', 0)}</dd></div>
        <div><dt>Puissance</dt><dd>{fmtDecimal(u.power_w, 'W', 0)}</dd></div>
        <div><dt>Entrée</dt><dd>{fmtDecimal(u.input_v, 'V', 0)}</dd></div>
        <div><dt>Sortie</dt><dd>{fmtDecimal(u.output_v, 'V', 0)}</dd></div>
        <div><dt>Batterie</dt><dd>{fmtDecimal(u.battery_v, 'V')}</dd></div>
      </dl>
      <Lu age={u.data_age_s} source="Onduleur (NUT)" />
    </article>
  )
}

/* ─────────────────────────────── Portes (UniFi Access) */

function Acces({ a, details }: { a: AccesUnifi | null; details: boolean }) {
  if (!a) {
    return (
      <Vide titre="Aucune mesure UniFi Access lue par Sonde pour l’instant.">
        <p>Sonde publie ici les portes du club dès que sa clé UniFi Access répond (Intégrations › Sonde › Clés des comptes lecteur). Aucune porte n’est supposée ouverte ni fermée en attendant.</p>
      </Vide>
    )
  }
  const ton = tonAcces(a)
  return (
    <>
      <Tuiles>
        <Tuile libelle="Appareils Access en ligne" valeur={fmtRatio(a.en_ligne, a.total)} ton={ton}
               detail={a.perime ? 'donnée périmée' : a.total === null ? 'pas encore relevé' : a.en_ligne !== null && a.en_ligne < a.total ? `${a.total - a.en_ligne} ne répond${a.total - a.en_ligne > 1 ? 'ent' : ''} pas` : 'lecteurs, contrôleurs et gâches'}
               source="UniFi Access" age={a.age_s} />
        <Tuile libelle="Portes suivies" valeur={fmtNombre(a.portes.length)} ton={a.portes.length ? 'neutre' : 'gris'}
               detail="gâche et capteur de position" source="UniFi Access" age={a.age_s} />
        {a.introuvables !== null && a.introuvables > 0 ? (
          <Tuile libelle="Postes introuvables dans Access" valeur={fmtNombre(a.introuvables)} ton={a.perime ? 'gris' : 'warn'}
                 detail="au registre de Sonde, mais absents d’UniFi Access" source="UniFi Access" age={a.age_s} />
        ) : null}
      </Tuiles>
      {a.portes.length === 0 ? <p className="lc-hint">UniFi Access répond, mais ne décrit encore aucune porte.</p> : (
        <ul className="sv-dom-lignes sv-bat-portes">
          {a.portes.map((p, i) => (
            <li key={p.cle} className={`sv-dom-ligne sv-bat-porte${p.perime ? ' sv-bat-porte--perime' : ''}`}>
              <span className="sv-dom-ligne__nom">{p.nom ?? `Porte ${i + 1}`}{details ? <span className="sv-dom-mono"> {p.id.slice(0, 8)}</span> : null}</span>
              <span className="sv-bat-porte__etats">
                <span className={`sv-bat-porte__etat${p.verrouillee === null || p.perime ? ' sv-bat-porte__etat--inconnu' : ''}`}>
                  {p.verrouillee === false ? <LockOpen size={15} aria-hidden="true" /> : <Lock size={15} aria-hidden="true" />}
                  {p.verrouillee === null ? 'Gâche : —' : p.verrouillee ? 'Verrouillée' : 'Déverrouillée'}
                </span>
                <span className={`sv-bat-porte__etat${p.ouverte === null || p.perime ? ' sv-bat-porte__etat--inconnu' : ''}`}>
                  {p.ouverte === true ? <DoorOpen size={15} aria-hidden="true" /> : <DoorClosed size={15} aria-hidden="true" />}
                  {p.ouverte === null ? 'Position non remontée' : p.ouverte ? 'Ouverte' : 'Fermée'}
                </span>
              </span>
              <span className="sv-dom-ligne__meta">{p.perime ? 'Donnée périmée · ' : ''}<Lu age={p.age_s} source="UniFi Access" /></span>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

/* ─────────────────────────────── Home Assistant */

function HomeAssistant({ d }: { d: ServiceDoc }) {
  const s = d.service
  const h = santeHa(s)
  const problemes = s.problems.map((p) => texteAutorise(p)).filter((p): p is string => p !== null)
  return (
    <>
      <p className="sv-bat-calme"><Pastille ton={TON_SERVICE[s.state] ?? 'gris'}>{s.state_label}</Pastille>{s.showing_lkg ? <span className="lc-hint"> · dernières données connues</span> : null}</p>
      <Tuiles>
        <Tuile libelle="Home Assistant" valeur={h.en_marche === null ? '—' : h.en_marche ? 'En marche' : 'Arrêté'}
               ton={h.en_marche === null ? 'gris' : h.en_marche ? 'ok' : 'err'} detail={h.api_ms != null ? `répond en ${fmtDecimal(h.api_ms, 'ms', 0)}` : undefined}
               source="Home Assistant" age={s.data_age_s} />
        <Tuile libelle="Entités suivies" valeur={fmtNombre(h.suivies)} ton={h.suivies === null ? 'gris' : 'neutre'}
               detail={h.entites != null ? `sur ${fmtNombre(h.entites)} entités` : undefined} source="Home Assistant" age={s.data_age_s} />
        <Tuile libelle="Indisponibles" valeur={fmtNombre(h.indisponibles)} ton={h.indisponibles === null ? 'gris' : h.indisponibles > 0 ? 'warn' : 'ok'}
               detail="entités suivies qui ne répondent plus" source="Home Assistant" age={s.data_age_s} />
      </Tuiles>
      {problemes.length ? (
        <ul className="sv-bat-problemes">{problemes.map((p) => <li key={p}>{p}</li>)}</ul>
      ) : null}
    </>
  )
}

/* ─────────────────────────────── Énergie */

function Energie({ d, details }: { d: PowerDoc; details: boolean }) {
  const liste = compteurs(d.generic)
  const puissances = liste.filter((c) => c.mesure === 'puissance')
  const energies = liste.filter((c) => c.mesure === 'energie')
  if (liste.length === 0) {
    return <Vide titre="Aucun compteur d’énergie lu." versADecider><p>Les compteurs que Home Assistant connaît attendent d’être validés.</p></Vide>
  }
  return (
    <>
      {puissances.length ? (
        <section className="sv-bat-compteurs" aria-labelledby="sv-bat-puissances">
          <h3 className="sv-bat-sous-titre" id="sv-bat-puissances">Puissance appelée</h3>
          <ul className="sv-bat-mesures">
            {puissances.map((c) => (
              <li key={c.cle}>
                <span className="sv-bat-mesures__nom">{c.nom}{details ? <span className="sv-dom-mono"> {c.cle.split(':batiment')[0]}</span> : null}</span>
                <span className="sv-bat-mesures__valeur">{fmtDecimal(c.valeur, c.unite ?? 'W', 0)}</span>
                <Lu age={c.age_s} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {energies.length ? (
        <section className="sv-bat-compteurs" aria-labelledby="sv-bat-energies">
          <h3 className="sv-bat-sous-titre" id="sv-bat-energies">Énergie consommée (écart)</h3>
          <ul className="sv-bat-mesures">
            {energies.map((c) => (
              <li key={c.cle}>
                <span className="sv-bat-mesures__nom">{c.nom}</span>
                <span className="sv-bat-mesures__valeur">{fmtDecimal(c.valeur, c.unite ?? 'kWh', 3)}</span>
                <Lu age={c.age_s} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  )
}

/* ─────────────────────────────── Courant des salles, PoE */

function SallesCourant({ d }: { d: PowerDoc }) {
  const menuSalle = useMenuSalle()
  if (d.zones.length === 0) return <p className="lc-hint">Aucune salle déclarée dans Sonde.</p>
  return (
    <ul className="sv-dom-lignes">
      {d.zones.map((z) => (
        <li key={z.code} className={`sv-dom-ligne${z.power.situation === 'off_unexpected' || z.power.situation === 'mains_outage' ? ' sv-dom-ligne--err' : ''}`}>
          <span className="sv-dom-ligne__nom"><Link className="sv-dom-lien" href={lienSalle(z.code)} {...menuSalle.salle({ code: z.code, label: z.label })}>{nomSalle(z.code, z.label)}</Link></span>
          <Pastille ton={TON_ALIM[z.power.situation] ?? 'gris'}>{z.power.label_fr}</Pastille>
          <span className="sv-dom-ligne__meta">{z.power.since ? `Depuis ${fmtDateHeure(z.power.since)}` : 'Depuis : —'}{z.power.source === 'unavailable' ? ' · témoin de courant non lu' : ''}</span>
        </li>
      ))}
    </ul>
  )
}

function Poe({ d }: { d: PowerDoc }) {
  if (d.poe.length === 0) return <p className="lc-hint">Aucun budget PoE lu sur les switchs.</p>
  const tries = [...d.poe].sort((a, z) => (z.pct ?? -1) - (a.pct ?? -1))
  return (
    <ul className="sv-bat-poe">
      {tries.map((p) => {
        const ton = tonMesure(p.level)
        return (
          <li key={p.device.uid} className="sv-bat-poe__ligne">
            <span className="sv-bat-poe__nom"><LienAppareil uid={p.device.uid} nom={p.device.display_name} zone={p.device.zone_code} /></span>
            <span className="sv-bat-poe__valeur">{fmtDecimal(p.used_w, 'W', 0)} / {fmtDecimal(p.budget_w, 'W', 0)}</span>
            <span className={`sv-bat-poe__barre sv-bat-poe__barre--${ton}`} aria-hidden="true"><span style={{ width: `${Math.max(0, Math.min(100, p.pct ?? 0))}%` }} /></span>
            <span className="sv-bat-poe__meta">{fmtDecimal(p.pct, '%')} · <Lu age={p.data_age_s} /></span>
          </li>
        )
      })}
    </ul>
  )
}
